import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
// Session-free client: a stale login token in localStorage must never
// poison public reads (the careers/survey "empty page" bug class).
import { publicSupabase as supabase } from '../lib/supabase';
import { gsap } from 'gsap';
import BranchMap from '../components/branches/BranchMap';
import BranchCard from '../components/branches/BranchCard';
import { DataState, CardSkeleton } from '../components/ui';
import { calculateDistance } from '../lib/format';

// Available amenities (stored in TEXT[] in database)
const AMENITY_KEYS = ['mushola', 'wifi', 'valet', 'lesehan', 'live_music', 'toilets', 'parking', 'ac', 'outdoor'];

// Animated Photo Reel Component - shows when no branch is selected
function PhotoReel({ branches, onPhotoClick }) {
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const animationRef = useRef(null);

  // Collect all gallery photos with their branch info
  const allPhotos = useMemo(() => {
    const photos = [];
    branches.forEach(branch => {
      // Add featured image
      if (branch.featured_image_url) {
        photos.push({
          url: branch.featured_image_url,
          branch,
          isCover: true,
        });
      }
      // Add gallery images
      if (branch.gallery_urls && branch.gallery_urls.length > 0) {
        branch.gallery_urls.forEach(url => {
          photos.push({
            url,
            branch,
            isCover: false,
          });
        });
      }
    });
    return photos;
  }, [branches]);

  // Setup infinite scroll animation with GSAP
  useEffect(() => {
    if (!trackRef.current || allPhotos.length === 0) return;

    const track = trackRef.current;
    const totalWidth = track.scrollWidth / 2; // Half because we duplicate

    // Kill any existing animation
    if (animationRef.current) {
      animationRef.current.kill();
    }

    // Create infinite scroll animation
    animationRef.current = gsap.to(track, {
      x: -totalWidth,
      duration: allPhotos.length * 3, // Speed based on number of photos
      ease: 'none',
      repeat: -1,
    });

    // Pause on hover
    const handleMouseEnter = () => animationRef.current?.pause();
    const handleMouseLeave = () => animationRef.current?.play();

    track.addEventListener('mouseenter', handleMouseEnter);
    track.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      animationRef.current?.kill();
      track.removeEventListener('mouseenter', handleMouseEnter);
      track.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [allPhotos]);

  if (allPhotos.length === 0) return null;

  // Duplicate photos for seamless loop
  const displayPhotos = [...allPhotos, ...allPhotos];

  return (
    <div ref={containerRef} className="relative overflow-hidden bg-gray-100 rounded-xl">
      <div className="absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-gray-100 to-transparent z-10" />
      <div className="absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-gray-100 to-transparent z-10" />

      <div ref={trackRef} className="flex gap-4 py-4 px-2" style={{ width: 'max-content' }}>
        {displayPhotos.map((photo, index) => (
          <div
            key={`${photo.branch.id}-${photo.url}-${index}`}
            onClick={() => onPhotoClick(photo.branch)}
            className="relative group cursor-pointer flex-shrink-0"
          >
            <div className="w-64 h-44 rounded-lg overflow-hidden shadow-md transition-transform duration-300 group-hover:scale-105 group-hover:shadow-xl">
              <img
                src={photo.url}
                alt={photo.branch.name}
                className="w-full h-full object-cover"
              />
              {/* Overlay with branch name */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
                  <p className="font-semibold text-sm truncate">{photo.branch.name}</p>
                  <p className="text-xs text-white/80">{photo.branch.city}</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Branch Gallery Modal Component
function BranchGalleryModal({ branch, onClose }) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { t } = useTranslation();

  // Combine featured image with gallery
  const allImages = useMemo(() => {
    const images = [];
    if (branch.featured_image_url) {
      images.push(branch.featured_image_url);
    }
    if (branch.gallery_urls && branch.gallery_urls.length > 0) {
      images.push(...branch.gallery_urls);
    }
    return images;
  }, [branch]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') setSelectedIndex(i => Math.max(0, i - 1));
      if (e.key === 'ArrowRight') setSelectedIndex(i => Math.min(allImages.length - 1, i + 1));
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [allImages.length, onClose]);

  if (allImages.length === 0) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex flex-col" onClick={onClose}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 text-white">
        <div>
          <h3 className="font-semibold text-lg">{branch.name}</h3>
          <p className="text-white/70 text-sm">{branch.city}</p>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-white/10 rounded-full transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Main Image */}
      <div
        className="flex-1 flex items-center justify-center px-4"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => setSelectedIndex(i => Math.max(0, i - 1))}
          disabled={selectedIndex === 0}
          className="p-3 text-white hover:bg-white/10 rounded-full transition-colors disabled:opacity-30"
        >
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <div className="flex-1 max-w-5xl mx-4">
          <img
            src={allImages[selectedIndex]}
            alt={`${branch.name} - ${selectedIndex + 1}`}
            className="max-h-[70vh] w-full object-contain rounded-lg"
          />
        </div>

        <button
          onClick={() => setSelectedIndex(i => Math.min(allImages.length - 1, i + 1))}
          disabled={selectedIndex === allImages.length - 1}
          className="p-3 text-white hover:bg-white/10 rounded-full transition-colors disabled:opacity-30"
        >
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Thumbnails */}
      <div className="p-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-center gap-2 overflow-x-auto pb-2">
          {allImages.map((url, index) => (
            <button
              key={url}
              onClick={() => setSelectedIndex(index)}
              className={`flex-shrink-0 w-20 h-14 rounded-md overflow-hidden transition-all ${
                index === selectedIndex
                  ? 'ring-2 ring-primary-500 scale-110'
                  : 'opacity-60 hover:opacity-100'
              }`}
            >
              <img src={url} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
        <p className="text-center text-white/60 text-sm mt-2">
          {selectedIndex + 1} / {allImages.length}
        </p>
      </div>
    </div>
  );
}

// Branch Gallery Strip - inline preview when branch is selected
function BranchGalleryStrip({ branch, onOpenGallery }) {
  const { t } = useTranslation();

  const allImages = useMemo(() => {
    const images = [];
    if (branch.featured_image_url) {
      images.push(branch.featured_image_url);
    }
    if (branch.gallery_urls && branch.gallery_urls.length > 0) {
      images.push(...branch.gallery_urls);
    }
    return images;
  }, [branch]);

  if (allImages.length <= 1) return null;

  return (
    <div className="bg-white rounded-xl shadow-sm p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-900">{t('branches.gallery')}</h3>
        <button
          onClick={() => onOpenGallery(branch)}
          className="text-primary-600 hover:text-primary-700 text-sm font-medium"
        >
          {t('branches.viewAll')} ({allImages.length})
        </button>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-2">
        {allImages.slice(0, 6).map((url, index) => (
          <button
            key={url}
            onClick={() => onOpenGallery(branch)}
            className="flex-shrink-0 w-24 h-20 rounded-lg overflow-hidden hover:opacity-90 transition-opacity"
          >
            <img src={url} alt="" className="w-full h-full object-cover" />
          </button>
        ))}
        {allImages.length > 6 && (
          <button
            onClick={() => onOpenGallery(branch)}
            className="flex-shrink-0 w-24 h-20 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-gray-200 transition-colors"
          >
            +{allImages.length - 6}
          </button>
        )}
      </div>
    </div>
  );
}

// Amenity filter button component
function AmenityFilterButton({ amenityKey, label, isActive, onClick }) {
  const icons = {
    mushola: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2L2 8v2h20V8L12 2zm0 2.5L18.5 8h-13L12 4.5zM4 12v8h3v-6h2v6h2v-6h2v6h2v-6h2v6h3v-8H4z"/>
      </svg>
    ),
    wifi: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z"/>
      </svg>
    ),
    valet: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
      </svg>
    ),
    lesehan: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M4 18v3h3v-3h10v3h3v-6H4v3zm15-8h3v3h-3v-3zM2 10h3v3H2v-3zm15 3H7V5c0-1.1.9-2 2-2h6c1.1 0 2 .9 2 2v8z"/>
      </svg>
    ),
    live_music: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
      </svg>
    ),
    toilets: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M5.5 22v-7.5H4V9c0-1.1.9-2 2-2h3c1.1 0 2 .9 2 2v5.5H9.5V22h-4zM18 22v-6h3l-2.54-7.63C18.18 7.55 17.42 7 16.56 7h-.12c-.86 0-1.63.55-1.9 1.37L12 16h3v6h3zM7.5 6c1.11 0 2-.89 2-2s-.89-2-2-2-2 .89-2 2 .89 2 2 2zm9 0c1.11 0 2-.89 2-2s-.89-2-2-2-2 .89-2 2 .89 2 2 2z"/>
      </svg>
    ),
    parking: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M13 3H6v18h4v-6h3c3.31 0 6-2.69 6-6s-2.69-6-6-6zm.2 8H10V7h3.2c1.1 0 2 .9 2 2s-.9 2-2 2z"/>
      </svg>
    ),
    ac: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M22 11h-4.17l3.24-3.24-1.41-1.42L15 11h-2V9l4.66-4.66-1.42-1.41L13 6.17V2h-2v4.17L7.76 2.93 6.34 4.34 11 9v2H9L4.34 6.34 2.93 7.76 6.17 11H2v2h4.17l-3.24 3.24 1.41 1.42L9 13h2v2l-4.66 4.66 1.42 1.41L11 17.83V22h2v-4.17l3.24 3.24 1.42-1.41L13 15v-2h2l4.66 4.66 1.41-1.42L17.83 13H22z"/>
      </svg>
    ),
    outdoor: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M14 6l-4.22 5.63 1.25 1.67L14 9.33 19 16h-8.46l-4.01-5.37L1 18h22L14 6zM5 16l1.52-2.03L8.04 16H5z"/>
      </svg>
    ),
  };

  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
        isActive
          ? 'bg-primary-500 text-white shadow-md'
          : 'bg-white text-gray-600 border border-gray-200 hover:border-primary-300 hover:text-primary-600'
      }`}
    >
      {icons[amenityKey] || <span className="w-4 h-4" />}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

export default function Branches() {
  const { t } = useTranslation();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [activeAmenityFilters, setActiveAmenityFilters] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [galleryBranch, setGalleryBranch] = useState(null);
  const headerRef = useRef(null);
  const filtersRef = useRef(null);
  const mapSectionRef = useRef(null);

  // Animate header on mount
  useEffect(() => {
    if (!loading && headerRef.current) {
      gsap.fromTo(
        headerRef.current.children,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.8, stagger: 0.15, ease: 'power3.out' }
      );
    }
  }, [loading]);

  // Animate filters
  useEffect(() => {
    if (!loading && filtersRef.current) {
      gsap.fromTo(
        filtersRef.current,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, delay: 0.3, ease: 'power3.out' }
      );
    }
  }, [loading]);

  // Animate map section
  useEffect(() => {
    if (!loading && mapSectionRef.current) {
      gsap.fromTo(
        mapSectionRef.current,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.7, delay: 0.4, ease: 'power3.out' }
      );
    }
  }, [loading]);

  // Get user's location on mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          console.log('Geolocation error:', error.message);
        },
        { timeout: 10000, maximumAge: 60000 }
      );
    }
  }, []);

  useEffect(() => {
    fetchBranches();
  }, [userLocation]);

  const fetchBranches = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('website_branches')
        .select(`
          id,
          management_id,
          name,
          slug,
          address,
          city,
          region,
          country,
          latitude,
          longitude,
          phone,
          email,
          open_hours,
          amenities,
          services,
          featured_image_url,
          gallery_urls,
          google_maps_url,
          is_featured,
          is_active
        `)
        .eq('is_active', true)
        .order('is_featured', { ascending: false })
        .order('name');

      if (error) throw error;

      // Calculate distance if user location is available
      let branchesWithDistance = data || [];
      if (userLocation) {
        branchesWithDistance = data.map(branch => {
          if (branch.latitude && branch.longitude) {
            const distance = calculateDistance(
              userLocation.lat,
              userLocation.lng,
              parseFloat(branch.latitude),
              parseFloat(branch.longitude)
            );
            return { ...branch, distance_km: distance };
          }
          return branch;
        }).sort((a, b) => (a.distance_km || 999) - (b.distance_km || 999));
      }

      setBranches(branchesWithDistance);
    } catch (err) {
      console.error('Error fetching branches:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  // Get unique regions and countries from branches
  const regions = useMemo(() => {
    const regionSet = new Set(branches.map(b => b.region).filter(Boolean));
    return Array.from(regionSet).sort();
  }, [branches]);

  const countries = useMemo(() => {
    const countrySet = new Set(branches.map(b => b.country).filter(Boolean));
    return Array.from(countrySet).sort();
  }, [branches]);

  // Get available amenities from all branches
  const availableAmenities = useMemo(() => {
    const amenitySet = new Set();
    branches.forEach(branch => {
      (branch.amenities || []).forEach(a => amenitySet.add(a));
    });
    return AMENITY_KEYS.filter(key => amenitySet.has(key));
  }, [branches]);

  // Filter branches based on search, region, country, and amenities
  const filteredBranches = useMemo(() => {
    return branches.filter((branch) => {
      // Search filter
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        (branch.name || '').toLowerCase().includes(query) ||
        (branch.address || '').toLowerCase().includes(query) ||
        (branch.city || '').toLowerCase().includes(query) ||
        (branch.region || '').toLowerCase().includes(query);

      // Region filter
      const matchesRegion = !selectedRegion || branch.region === selectedRegion;

      // Country filter
      const matchesCountry = !selectedCountry || branch.country === selectedCountry;

      // Amenity filters (branch.amenities is TEXT[])
      const matchesAmenities =
        activeAmenityFilters.length === 0 ||
        activeAmenityFilters.every(filter => (branch.amenities || []).includes(filter));

      return matchesSearch && matchesRegion && matchesCountry && matchesAmenities;
    });
  }, [branches, searchQuery, selectedRegion, selectedCountry, activeAmenityFilters]);

  const toggleAmenityFilter = (key) => {
    setActiveAmenityFilters((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedRegion('');
    setSelectedCountry('');
    setActiveAmenityFilters([]);
  };

  const hasActiveFilters = searchQuery || selectedRegion || selectedCountry || activeAmenityFilters.length > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div ref={headerRef} className="text-center mb-6">
            <h1 className="section-title">{t('branches.title')}</h1>
            <p className="section-subtitle">{t('branches.subtitle')}</p>
          </div>

          {/* Search & Filters */}
          <div ref={filtersRef} className="space-y-4">
            {/* Search Bar & Region/Country Filter */}
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search */}
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder={t('branches.search')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="input-field pl-10"
                />
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>

              {/* Country Dropdown */}
              {countries.length > 1 && (
                <div className="sm:w-40">
                  <select
                    value={selectedCountry}
                    onChange={(e) => setSelectedCountry(e.target.value)}
                    className="input-field"
                  >
                    <option value="">{t('branches.allCountries')}</option>
                    {countries.map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Region Dropdown */}
              <div className="sm:w-48">
                <select
                  value={selectedRegion}
                  onChange={(e) => setSelectedRegion(e.target.value)}
                  className="input-field"
                >
                  <option value="">{t('branches.allRegions')}</option>
                  {regions.map((region) => (
                    <option key={region} value={region}>
                      {region}
                    </option>
                  ))}
                </select>
              </div>

              {/* Filter Toggle Button (Mobile) */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="sm:hidden flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                {t('branches.filterByAmenities')}
                {activeAmenityFilters.length > 0 && (
                  <span className="bg-primary-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {activeAmenityFilters.length}
                  </span>
                )}
              </button>
            </div>

            {/* Amenity Filters (Desktop always visible, Mobile toggled) */}
            {availableAmenities.length > 0 && (
              <div className={`${showFilters ? 'block' : 'hidden'} sm:block`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-gray-500 mr-1">{t('branches.filterByAmenities')}:</span>
                  {availableAmenities.map((key) => (
                    <AmenityFilterButton
                      key={key}
                      amenityKey={key}
                      label={t(`branches.amenities.${key}`)}
                      isActive={activeAmenityFilters.includes(key)}
                      onClick={() => toggleAmenityFilter(key)}
                    />
                  ))}
                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className="ml-2 text-sm text-gray-500 hover:text-gray-700 underline"
                    >
                      {t('common.clearAll')}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Results Count & User Location Status */}
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>{t('branches.branchCount', { count: filteredBranches.length })}</span>
              {userLocation && (
                <span className="flex items-center gap-1 text-green-600">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  </svg>
                  {t('branches.sortedByDistance')}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Photo Reel - Show when no branch is selected */}
      {!selectedBranch && branches.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="mb-3">
            <p className="text-sm text-gray-500 text-center">{t('branches.clickPhotoToSelect')}</p>
          </div>
          <PhotoReel branches={branches} onPhotoClick={setSelectedBranch} />
        </div>
      )}

      {/* Map & List Section */}
      <div ref={mapSectionRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Map - Takes more space */}
          <div className="lg:col-span-3 h-[400px] lg:h-[calc(100vh-300px)] min-h-[500px] rounded-xl overflow-hidden shadow-lg bg-white">
            <BranchMap
              branches={filteredBranches}
              selectedBranch={selectedBranch}
              onBranchSelect={setSelectedBranch}
              userLocation={userLocation}
            />
          </div>

          {/* Branch List & Gallery */}
          <div className="lg:col-span-2 space-y-3 lg:h-[calc(100vh-300px)] lg:min-h-[500px] lg:overflow-y-auto lg:pr-2">
            {/* Branch Gallery Strip - Show when branch is selected */}
            {selectedBranch && (
              <BranchGalleryStrip
                branch={selectedBranch}
                onOpenGallery={setGalleryBranch}
              />
            )}
            <DataState
              loading={loading}
              error={error}
              empty={filteredBranches.length === 0}
              onRetry={fetchBranches}
              skeleton={
                <div className="space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <CardSkeleton key={i} />
                  ))}
                </div>
              }
              emptyProps={{
                icon: '📍',
                message: t('branches.noBranchesFound'),
                action: hasActiveFilters ? (
                  <button
                    onClick={clearFilters}
                    className="text-primary-600 hover:text-primary-700 font-medium"
                  >
                    {t('common.clearFilters')}
                  </button>
                ) : null,
              }}
            >
              {filteredBranches.map((branch) => (
                <BranchCard
                  key={branch.id}
                  branch={branch}
                  isSelected={selectedBranch?.id === branch.id}
                  onClick={() => setSelectedBranch(branch)}
                />
              ))}
            </DataState>
          </div>
        </div>
      </div>

      {/* Gallery Modal */}
      {galleryBranch && (
        <BranchGalleryModal
          branch={galleryBranch}
          onClose={() => setGalleryBranch(null)}
        />
      )}
    </div>
  );
}
